import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	EventEmitter,
	HostListener,
	Input,
	inject,
	OnChanges,
	Output,
	PLATFORM_ID,
	SimpleChanges,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MfaService } from '@services/auth/mfa.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';

@Component({
	selector: 'app-mfa-enrollment-dialog',
	imports: [SharedModule, ButtonComponent, SpinnerComponent],
	templateUrl: './mfa-enrollment-dialog.component.html',
	styleUrl: './mfa-enrollment-dialog.component.scss',
})
export class MfaEnrollmentDialogComponent implements OnChanges {
	@Input() visible = false;
	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() mfaStatusChanged = new EventEmitter<void>();

	mfaQrCode: string | null = null;
	mfaSecret: string | null = null;
	mfaFactorId: string | null = null;
	mfaCodeDigits: string[] = ['', '', '', '', '', ''];
	isEnrollingMfa = false;
	isVerifyingMfa = false;

	private readonly mfaService = inject(MfaService);
	private readonly messageService = inject(MessageAlertService);
	private readonly translocoService = inject(TranslocoService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly viewportService = inject(ViewportService);

	isDesktopView(): boolean {
		return !this.viewportService.isMobileView();
	}

	/** Escape closes the mobile bottom sheet (the desktop p-dialog handles it itself). */
	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.visible && !this.isDesktopView() && !this.isVerifyingMfa) {
			this.close();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible'] && changes['visible'].currentValue === true && !changes['visible'].previousValue) {
			// When dialog becomes visible, start enrollment
			this.enrollInMFA();
		}
	}

	/**
	 * Enrolls user in MFA
	 */
	private enrollInMFA(): void {
		this.isEnrollingMfa = true;
		this.mfaService.enroll().subscribe({
			next: (response) => {
				this.mfaQrCode = response.totp.qr_code;
				this.mfaSecret = response.totp.secret;
				this.mfaFactorId = response.id;
				this.isEnrollingMfa = false;
				// Don't emit visibleChange here - dialog is already visible
			},
			error: (error) => {
				console.error('Error enrolling in MFA:', error);
				this.isEnrollingMfa = false;
				// Close dialog on error
				this.visible = false;
				this.visibleChange.emit(false);
				this.mfaStatusChanged.emit();

				const errorMsg = translateApiError(error, this.translocoService, 'profile.mfa.errorEnrolling');
				this.messageService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					errorMsg,
				);
			},
		});
	}

	/**
	 * Verifies MFA enrollment with code
	 */
	verifyMfaCode(): void {
		const code = this.mfaCodeDigits.join('');

		if (!this.mfaFactorId || !code || code.length !== 6) {
			this.messageService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('profile.mfa.enterCode'),
			);
			return;
		}

		this.isVerifyingMfa = true;
		this.mfaService.verifyEnrollment(this.mfaFactorId, code).subscribe({
			next: () => {
				this.messageService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('profile.mfa.enabledSuccess'),
				);
				this.isVerifyingMfa = false;
				// Close without unenrolling (verification was successful)
				this.closeWithoutUnenroll();
				this.mfaStatusChanged.emit();
			},
			error: (error) => {
				console.error('Error verifying MFA:', error);
				this.isVerifyingMfa = false;
				this.mfaCodeDigits = ['', '', '', '', '', ''];
				this.messageService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('profile.mfa.invalidCode'),
				);
			},
		});
	}

	/**
	 * Closes MFA enrollment dialog and unenrolls unverified factor
	 */
	close(): void {
		// If there's an unverified factor, remove it
		if (this.mfaFactorId) {
			this.mfaService.unenroll(this.mfaFactorId).subscribe({
				next: () => {},
				error: (error) => {
					console.error('Error cleaning up unverified factor:', error);
				},
			});
		}

		this.closeWithoutUnenroll();
		this.mfaStatusChanged.emit();
	}

	/**
	 * Closes dialog without unenrolling (used after successful verification)
	 */
	private closeWithoutUnenroll(): void {
		this.resetState();
		this.visible = false;
		this.visibleChange.emit(false);
	}

	/**
	 * Resets component state
	 */
	private resetState(): void {
		this.mfaQrCode = null;
		this.mfaSecret = null;
		this.mfaFactorId = null;
		this.mfaCodeDigits = ['', '', '', '', '', ''];
	}

	/**
	 * Handles input in MFA code boxes
	 */
	onMfaDigitInput(event: Event, index: number): void {
		if (!isPlatformBrowser(this.platformId)) return;

		const input = event.target as HTMLInputElement;
		const value = input.value;

		// Only allow numbers
		const numericValue = value.replace(/\D/g, '');
		const singleDigit = numericValue.length > 0 ? numericValue.slice(-1) : '';

		// Update input value and model
		input.value = singleDigit;
		this.mfaCodeDigits[index] = singleDigit;

		// Auto-focus next input if we have a digit
		if (singleDigit && index < 5) {
			// Find the next sibling input in the same container
			const container = input.closest('.mfa-code-boxes');
			if (container) {
				const inputs = container.querySelectorAll<HTMLInputElement>('.mfa-code-input');
				const nextInput = inputs[index + 1];
				if (nextInput) {
					setTimeout(() => {
						nextInput.focus();
					}, 0);
				}
			}
		}
	}

	/**
	 * Handles keydown in MFA code boxes
	 */
	onMfaDigitKeydown(event: KeyboardEvent, index: number): void {
		if (!isPlatformBrowser(this.platformId)) return;

		const input = event.target as HTMLInputElement;
		const container = input.closest('.mfa-code-boxes');
		const inputs = container?.querySelectorAll<HTMLInputElement>('.mfa-code-input');

		// Handle Enter key to submit
		if (event.key === 'Enter') {
			event.preventDefault();
			const code = this.mfaCodeDigits.join('');
			if (code.length === 6) {
				this.verifyMfaCode();
			}
			return;
		}

		// Handle backspace
		if (event.key === 'Backspace') {
			if (!input.value && index > 0 && inputs) {
				event.preventDefault();
				const prevInput = inputs[index - 1];
				if (prevInput) {
					this.mfaCodeDigits[index - 1] = '';
					prevInput.value = '';
					prevInput.focus();
				}
			} else if (input.value) {
				this.mfaCodeDigits[index] = '';
			}
			return;
		}

		// If it's a number and the current field has a value, replace it
		if (/^[0-9]$/.test(event.key) && input.value) {
			input.value = '';
			this.mfaCodeDigits[index] = '';
		}
	}

	/**
	 * Handles paste event for MFA code
	 */
	onMfaPaste(event: ClipboardEvent): void {
		if (!isPlatformBrowser(this.platformId)) return;

		event.preventDefault();
		const pastedData = event.clipboardData?.getData('text') || '';
		const digits = pastedData.replace(/\D/g, '').slice(0, 6);

		const target = event.target as HTMLElement;
		const container = target.closest('.mfa-code-boxes');
		const inputs = container?.querySelectorAll<HTMLInputElement>('.mfa-code-input');

		if (inputs) {
			for (let i = 0; i < 6; i++) {
				this.mfaCodeDigits[i] = digits[i] || '';
				if (inputs[i]) {
					inputs[i].value = this.mfaCodeDigits[i];
				}
			}

			// Focus last filled input or first empty
			const focusIndex = Math.min(digits.length, 5);
			if (inputs[focusIndex]) {
				inputs[focusIndex].focus();
			}
		}
	}
}
